import { Fragment, useState } from "react";
import { History, Pencil, ChevronUp, AlertTriangle } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { IconButton } from "@/components/ui/IconButton";
import { Skeleton } from "@/components/ui/Skeleton";
import { LocationPicker } from "@/components/ui/LocationPicker";
import { useApi } from "@/lib/api";
import { cn } from "@/lib/cn";
import { CurrentPricesResponse, PriceRecord, PriceHistoryResponse } from "@/types/prices";
import { ChangePriceModal } from "./prices/ChangePriceModal";

// ---------------------------------------------------------------------------
// Static config
// ---------------------------------------------------------------------------

interface UtilityDef {
  key: string;
  label: string;
  defaultUnit: string;
  description: string;
}

const UTILITY_DEFS: UtilityDef[] = [
  { key: "en-el",               label: "Energia el.",         defaultUnit: "PLN/osoba", description: "Kwota rzeczywista pochodzi z faktury Tauron — ta cena służy tylko jako punkt odniesienia; kluczowa jest kwota szacunkowa poniżej" },
  { key: "gaz",                 label: "Gaz",                 defaultUnit: "PLN/m³",    description: "Kwota rzeczywista pochodzi z faktury PGNiG — ta cena służy tylko jako punkt odniesienia; kluczowa jest kwota szacunkowa poniżej" },
  { key: "zimna-woda",         label: "Zimna woda",          defaultUnit: "PLN/m³",    description: "Cena za m³ zimnej wody — przeliczana z licznika" },
  { key: "podgrzanie-cw",      label: "Ciepła woda",         defaultUnit: "PLN/m³",    description: "Cena za m³ ciepłej wody — przeliczana z licznika" },
  { key: "co",                 label: "CO",                  defaultUnit: "PLN/GJ",    description: "Cena za GJ ciepła (licznik) lub stała zaliczka miesięczna" },
  { key: "oplata-stała-co-cw", label: "Opłata stała CO/CW",  defaultUnit: "PLN/lokal", description: "Stała miesięczna opłata infrastrukturalna za CO/CW" },
  { key: "smieci",             label: "Śmieci",              defaultUnit: "PLN/osoba", description: "Stawka za osobę na miesiąc — mnoży przez liczbę lokatorów" },
  { key: "internet",           label: "Internet",            defaultUnit: "PLN/lokal", description: "Stała opłata za lokal" },
];

const UTIL_MAP = Object.fromEntries(UTILITY_DEFS.map((d) => [d.key, d]));

// Utilities whose real data can be missing at the collection deadline and
// therefore support a configurable flat-PLN estimated_value (see UC-U06).
const METERED_UTILITIES = new Set(["en-el", "gaz", "zimna-woda", "podgrzanie-cw", "co"]);

// Utilities billed from scraped invoices (Tauron/PGNiG): the unit price is
// irrelevant, only the estimated amount is used.
const SCRAPED_UTILITIES = new Set(["en-el", "gaz"]);

// Which utilities each location uses (determines rows shown per tab)
const LOCATION_UTILITIES: Record<string, string[]> = {
  krasinskiego: ["en-el", "gaz", "zimna-woda", "co", "smieci", "internet"],
  stanczyka:    ["en-el", "zimna-woda", "podgrzanie-cw", "co", "oplata-stała-co-cw", "smieci", "internet"],
  rydlowka:     ["en-el", "zimna-woda", "podgrzanie-cw", "co", "oplata-stała-co-cw", "smieci", "internet"],
};

const LOCATIONS = ["krasinskiego", "stanczyka", "rydlowka"];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtUnit(raw: string | undefined, defaultUnit: string): string {
  if (!raw) return defaultUnit;
  return raw.replace("/m3", "/m³").replace("/person", "/osoba").replace("/flat", "/lokal");
}

function fmtYM(ym: string | undefined): string {
  if (!ym) return "—";
  return ym; // already YYYY-MM
}

function fmtDate(iso: string | undefined): string {
  if (!iso) return "—";
  return iso.slice(0, 10);
}

// ---------------------------------------------------------------------------
// History panel (inline, fetched on demand)
// ---------------------------------------------------------------------------

function HistoryPanel({ location, utility }: { location: string; utility: string }) {
  const { data, loading, error } = useApi<PriceHistoryResponse>(
    `/api/utility/prices/history?location=${location}&utility=${utility}`
  );

  if (loading) return <div className="px-4 py-3 text-xs text-text-muted animate-pulse">Ładowanie historii…</div>;
  if (error)   return <div className="px-4 py-3 text-xs text-danger">Błąd: {error.message}</div>;
  if (!data?.history.length) return <div className="px-4 py-3 text-xs text-text-muted italic">Brak historii.</div>;

  return (
    <table className="w-full text-sm">
      <thead className="text-xs text-text-muted">
        <tr className="border-b border-border">
          <th className="text-left font-medium px-4 py-2">Obowiązuje od</th>
          <th className="text-right font-medium px-4 py-2">Cena</th>
          <th className="text-left font-medium px-4 py-2">Jedn.</th>
          <th className="text-left font-medium px-4 py-2 hidden sm:table-cell">Zmieniony</th>
          <th className="text-left font-medium px-4 py-2 hidden sm:table-cell">Notatka</th>
        </tr>
      </thead>
      <tbody>
        {data.history.map((h, idx) => (
          <tr key={h["year-month-location-utility"]}
              className={cn("border-t border-border", idx === 0 && "font-medium text-text")}>
            <td className="px-4 py-2 text-xs font-mono">{fmtYM(h["year-month"])}</td>
            <td className="px-4 py-2 text-xs font-mono text-right">{h.value}</td>
            <td className="px-4 py-2 text-xs text-text-muted">{fmtUnit(h.unit, "")}</td>
            <td className="px-4 py-2 text-xs text-text-muted hidden sm:table-cell">{fmtDate(h.lastModified)}</td>
            <td className="px-4 py-2 text-xs text-text-muted hidden sm:table-cell">{h.notes || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Price table for one location tab
// ---------------------------------------------------------------------------

interface PriceTableProps {
  location: string;
  prices: Record<string, PriceRecord>;  // utility → record (may be undefined)
  onEdit: (utility: string) => void;
}

function PriceTable({ location, prices, onEdit }: PriceTableProps) {
  const [expandedUtil, setExpandedUtil] = useState<string | null>(null);
  const utils = LOCATION_UTILITIES[location] ?? [];

  function toggleHistory(key: string) {
    setExpandedUtil((cur) => (cur === key ? null : key));
  }

  return (
    <Card padding="none">
      <table className="w-full text-sm">
        <thead className="text-xs text-text-muted">
          <tr className="border-b border-border">
            <th className="text-left font-medium px-4 py-2">Medium</th>
            <th className="text-right font-medium px-4 py-2">Cena</th>
            <th className="text-left font-medium px-4 py-2">Jednostka</th>
            <th className="text-left font-medium px-4 py-2 hidden sm:table-cell">Obowiązuje od</th>
            <th className="text-left font-medium px-4 py-2 hidden md:table-cell">Notatka</th>
            <th className="px-4 py-2 w-28"></th>
          </tr>
        </thead>
        <tbody>
          {utils.map((key) => {
            const def = UTIL_MAP[key];
            const rec = prices[key];
            const missing = !rec;
            const isExpanded = expandedUtil === key;

            return (
              <Fragment key={key}>
                <tr
                  className={cn(
                    "border-b border-border transition-colors",
                    missing ? "bg-warning/5" : "hover:bg-bg-subtle/50",
                    isExpanded && "bg-bg-subtle",
                  )}
                >
                  <td className="px-4 py-2">
                    <div className="text-xs font-medium">{def.label}</div>
                    <div className="text-xs text-text-muted hidden md:block">{def.description}</div>
                  </td>

                  <td className="px-4 py-2 text-xs text-right font-mono">
                    {SCRAPED_UTILITIES.has(key) ? null : missing ? (
                      <span className="text-warning flex items-center justify-end gap-1">
                        <AlertTriangle className="size-3.5" /> Brak
                      </span>
                    ) : (
                      rec.value
                    )}
                    {METERED_UTILITIES.has(key) && (
                      <div className="text-text-muted">
                        {rec?.estimated_value
                          ? `Szacunkowo: ${rec.estimated_value} PLN`
                          : "Brak kwoty szacunkowej"}
                      </div>
                    )}
                  </td>

                  <td className="px-4 py-2 text-xs text-text-muted">
                    {SCRAPED_UTILITIES.has(key)
                      ? "PLN"
                      : missing ? fmtUnit(undefined, def.defaultUnit) : fmtUnit(rec.unit, def.defaultUnit)}
                  </td>

                  <td className="px-4 py-2 text-xs hidden sm:table-cell">
                    {missing ? (
                      <Badge tone="warning">Nie ustawiona</Badge>
                    ) : (
                      <span className="font-mono">{fmtYM(rec["year-month"])}</span>
                    )}
                  </td>

                  <td className="px-4 py-2 text-xs text-text-muted hidden md:table-cell max-w-[160px] truncate"
                      title={rec?.notes}>
                    {rec?.notes || "—"}
                  </td>

                  <td className="px-4 py-2">
                    <div className="flex items-center justify-end gap-1">
                      {!missing && (
                        <IconButton
                          label={isExpanded ? "Ukryj historię" : "Pokaż historię"}
                          onClick={() => toggleHistory(key)}
                        >
                          {isExpanded
                            ? <ChevronUp className="size-3.5" />
                            : <History className="size-3.5" />}
                        </IconButton>
                      )}
                      <IconButton label="Zmień cenę" onClick={() => onEdit(key)}>
                        <Pencil className="size-3.5" />
                      </IconButton>
                    </div>
                  </td>
                </tr>

                {/* Inline history panel */}
                {isExpanded && (
                  <tr key={`${key}-history`} className="border-b border-border bg-bg-subtle">
                    <td colSpan={6} className="py-2">
                      <div className="text-xs text-text-muted px-4 pb-1 font-medium uppercase tracking-wide">
                        Historia cen — {def.label}
                      </div>
                      <HistoryPanel location={location} utility={key} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Root page
// ---------------------------------------------------------------------------

const LOCATION_LABELS: Record<string, string> = {
  krasinskiego: "Krasińskiego",
  stanczyka: "Stańczyka",
  rydlowka: "Rydlówka",
};

export default function Prices() {
  const [tick, setTick] = useState(0);
  const [location, setLocation] = useState<string | null>(null);
  const { data, loading, error } = useApi<CurrentPricesResponse>(
    "/api/utility/prices",
    [tick]
  );
  interface EditTarget { location: string; utility: string; }
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);

  function refetch() { setTick((n) => n + 1); }

  const missingCount = !data ? 0 : LOCATIONS.reduce((acc, loc) => {
    const utils = LOCATION_UTILITIES[loc] ?? [];
    return acc + utils.filter((u) => !SCRAPED_UTILITIES.has(u) && !data[loc]?.[u]).length;
  }, 0);

  const visibleLocations = location ? [location] : LOCATIONS;

  return (
    <div className="flex flex-col gap-4">
      {/* Control bar — matches Odczyty / Rozliczenie pattern */}
      <div className="flex flex-wrap items-center gap-3 pb-3 border-b border-border">
        <LocationPicker value={location} onChange={setLocation} />
        <div className="flex-1" />
        {missingCount > 0 && (
          <Badge tone="warning">
            <AlertTriangle className="size-3 mr-1" />
            {missingCount} brakujących
          </Badge>
        )}
      </div>

      {/* Price tables */}
      {loading && !data ? (
        <Skeleton className="h-64 w-full" />
      ) : error ? (
        <p className="text-sm text-danger">{error.message}</p>
      ) : (
        <div className="flex flex-col gap-4">
          {visibleLocations.map((loc) => (
            <div key={loc}>
              {/* Location label — only shown in "Wszystkie" view */}
              {!location && (
                <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">
                  {LOCATION_LABELS[loc] ?? loc}
                </h2>
              )}
              <PriceTable
                location={loc}
                prices={data?.[loc] ?? {}}
                onEdit={(utility) => setEditTarget({ location: loc, utility })}
              />
            </div>
          ))}
        </div>
      )}

      {/* Change price modal */}
      {editTarget && (
        <ChangePriceModal
          location={editTarget.location}
          utility={editTarget.utility}
          utilityLabel={UTIL_MAP[editTarget.utility]?.label ?? editTarget.utility}
          unit={fmtUnit(
            data?.[editTarget.location]?.[editTarget.utility]?.unit,
            UTIL_MAP[editTarget.utility]?.defaultUnit ?? "PLN"
          )}
          current={data?.[editTarget.location]?.[editTarget.utility]}
          onClose={() => setEditTarget(null)}
          onSaved={refetch}
        />
      )}
    </div>
  );
}
